import { MODULE_NAV } from '@/shell/moduleNav';
import type { UserSummary } from './api';
import { canAccessCoportalPage } from './visibility';

/**
 * The CS/OPS Portal page list the CLIENT is willing to gate on.
 *
 * `canAccessCoportalPage` shipped with the 51-checkbox rights matrix and had
 * no consumer at all — nothing imported it, so restricting a user to three
 * portal pages changed exactly nothing they could see or open. This module is
 * the missing half: one ordered slug list that both the rail filter and the
 * route guard read, so the two can never disagree.
 *
 * SCOPE, deliberately narrow. The list is derived from `MODULE_NAV.coportal`,
 * i.e. the pages that actually appear in the rail. The server registry also
 * holds hidden children (`agent-scores`, `nps-kuoni`, `log-prep`,
 * `hotline-schedule-edit`) and the portal owns a handful of routes that are
 * not registry pages at all (`help`, `about`, `change-password`, `theme`,
 * `directories`, `log-my`/`log-bn`/`log-sg`). Those are NOT gated here.
 *
 * The bias is intentional: a client-side gate adds no security — the API is
 * the authority (`CurrentUser.CanAccess`) — so its only job is to stop
 * showing and opening pages the admin took away. Guessing wrong in the strict
 * direction would lock a real user out of a page the server would have
 * served, which is strictly worse than the status quo. Anything the rail does
 * not list falls through.
 */

/** Route segment → portal page slug, or null when the route is not gate-able. */
export function coportalSlugFromPath(pathname: string): string | null {
  const parts = pathname.replace(/^\/+/, '').split('/').filter(Boolean);
  if (parts[0] !== 'coportal') return null;
  // `/coportal` and `/coportal/` both render the dashboard.
  return parts[1] ?? 'dashboard';
}

/**
 * Rail routes that are NOT a registry page themselves but front several.
 *
 * `/coportal/hotline-whatsapp` renders both company WhatsApp hotlines as
 * tabs, and each hotline is its own grant (`hotline-whatsapp-my`,
 * `hotline-whatsapp-sg`) so MY and SG can be handed out separately. The
 * route is reachable when ANY of its pages is; the page itself then shows
 * only the tabs the user holds (and the API 403s the rest).
 *
 * Alias routes are deliberately kept OUT of {@link COPORTAL_GATED_SLUGS}:
 * that list is registry slugs only (a drift test pins it), and it doubles
 * as the redirect-fallback order.
 */
export const COPORTAL_ROUTE_ALIASES: Readonly<Record<string, readonly string[]>> = {
  'hotline-whatsapp': ['hotline-whatsapp-my', 'hotline-whatsapp-sg'],
};

/** `true` when this route slug fronts several registry pages. */
export function isCoportalAliasRoute(slug: string | null): slug is string {
  return !!slug && Object.prototype.hasOwnProperty.call(COPORTAL_ROUTE_ALIASES, slug);
}

/** Ordered gate-able slugs, in rail order — the fallback target order too. */
export const COPORTAL_GATED_SLUGS: readonly string[] = (() => {
  const out: string[] = [];
  for (const group of MODULE_NAV.coportal) {
    for (const item of group.items) {
      const slug = coportalSlugFromPath(item.to);
      if (isCoportalAliasRoute(slug)) continue;
      if (slug && !out.includes(slug)) out.push(slug);
    }
  }
  // The rail has no Dashboard-less state; `/coportal` itself resolves to it.
  if (!out.includes('dashboard')) out.unshift('dashboard');
  return out;
})();

const GATED = new Set(COPORTAL_GATED_SLUGS);

/**
 * `free_access` portal pages — always viewable however restricted the user.
 * Mirrors the `free_access` column of `PageRegistryController._coportal`,
 * where My Notes is the only entry carrying it.
 */
export const COPORTAL_FREE_PAGES: ReadonlySet<string> = new Set(['my-notes']);

/** `true` when this slug is one the client is prepared to gate. */
export function isGatedCoportalPage(slug: string | null): slug is string {
  return !!slug && GATED.has(slug);
}

/**
 * Route-level twin of `canAccessCoportalPage`: an alias route passes when
 * ANY page it fronts does; every other slug is judged as itself. The rail
 * filter and `RequireCoportalPage` both call this so they cannot disagree.
 */
export function canAccessCoportalRoute(slug: string, user: UserSummary | null): boolean {
  const pages = COPORTAL_ROUTE_ALIASES[slug];
  if (pages) {
    return pages.some((p) => canAccessCoportalPage(p, COPORTAL_FREE_PAGES.has(p), user));
  }
  return canAccessCoportalPage(slug, COPORTAL_FREE_PAGES.has(slug), user);
}
